import { useState } from "react";
import { Phone, PhoneOff, Mic, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCallMonitor } from "@/hooks/useCallMonitor";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface CallMonitorWidgetProps {
  jobId?: string;
  leadId?: string;
  mode?: 'job' | 'lead' | 'new-lead';
}

export function CallMonitorWidget({ jobId, leadId, mode = 'new-lead' }: CallMonitorWidgetProps) {
  const { toast } = useToast();
  const { isActive, transcript, lastCallResults, startMonitoring, stopMonitoring } = useCallMonitor();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleStart = async () => {
    try {
      await startMonitoring();
      toast({ title: "Call started", description: "Bridgette is listening" });
    } catch (error) {
      toast({
        title: "Failed to start call",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleStop = async () => {
    setIsProcessing(true);
    try {
      await stopMonitoring({ jobId, leadId, mode });
      toast({
        title: "Call ended",
        description: `Created ${lastCallResults?.notes || 0} notes and ${lastCallResults?.tasks || 0} tasks`,
      });
    } catch (error) {
      toast({
        title: "Error ending call",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className={cn(
        "relative flex items-center gap-3 px-3 py-2.5 rounded-xl border backdrop-blur-xl transition-all overflow-hidden",
        isActive
          ? "border-red-500/40 bg-gradient-to-r from-red-500/10 via-red-500/5 to-transparent"
          : "border-primary/25 bg-gradient-to-r from-primary/10 via-purple-500/5 to-transparent hover:border-primary/50"
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          "relative h-9 w-9 shrink-0 rounded-lg flex items-center justify-center shadow-lg ring-1 ring-white/10",
          isActive
            ? "bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/30"
            : "bg-gradient-to-br from-primary to-purple-500 shadow-primary/30"
        )}
      >
        {isActive ? (
          <Mic className="h-4 w-4 text-white" />
        ) : (
          <Sparkles className="h-4 w-4 text-white" />
        )}
        {isActive && (
          <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-background" />
          </span>
        )}
      </div>

      {/* Label */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold truncate">
            {isActive ? "Bridgette is listening" : "Call with Bridgette"}
          </span>
          {isActive && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-500">Live</span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground truncate">
          {isActive
            ? `${transcript.length} message${transcript.length !== 1 ? 's' : ''} captured`
            : lastCallResults
            ? `Last call: ${lastCallResults.notes} notes · ${lastCallResults.tasks} tasks`
            : "AI auto-captures notes & tasks"}
        </p>
      </div>

      {/* Action */}
      {!isActive ? (
        <Button
          onClick={handleStart}
          size="sm"
          className="h-8 gap-1.5 shadow-md shadow-primary/20"
        >
          <Phone className="h-3.5 w-3.5" />
          Start
        </Button>
      ) : (
        <Button
          onClick={handleStop}
          size="sm"
          variant="destructive"
          disabled={isProcessing}
          className="h-8 gap-1.5"
        >
          {isProcessing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PhoneOff className="h-3.5 w-3.5" />}
          {isProcessing ? "Ending" : "End"}
        </Button>
      )}
    </div>
  );
}
